import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Zap, LogOut, User, Shield, MessageSquare, Users, Building2, Newspaper, Sparkles, Calendar, BarChart3, Rss, Award, BookOpen, Briefcase, Menu, LayoutDashboard, DollarSign, Bell } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const linkCls = ({ isActive }) =>
  `px-3 py-2 text-sm font-medium tracking-tight uppercase transition-colors ${
    isActive ? "text-[hsl(var(--nh-red))] underline decoration-2 underline-offset-4" : "text-black hover:text-[hsl(var(--nh-red))]"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const authed = user && typeof user === "object";
  const active = authed && user.subscription_active;

  // Close mobile drawer whenever the route changes
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const mobileLinks = [
    ...(authed ? [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "m-nav-dashboard" }] : []),
    { to: "/agencies", label: "Agencies", icon: Building2, testid: "m-nav-agencies" },
    { to: "/jobs", label: "Jobs", icon: Briefcase, testid: "m-nav-jobs" },
    { to: "/feed", label: "Feed", icon: Newspaper, testid: "m-nav-feed" },
    { to: "/forums", label: "Forums", icon: Users, testid: "m-nav-forums" },
    { to: "/nurses", label: "Nurses", icon: Users, testid: "m-nav-nurses" },
    { to: "/mentors", label: "Mentors", icon: Users, testid: "m-nav-mentors" },
    { to: "/ask", label: "Ask a Nurse", icon: MessageSquare, testid: "m-nav-ask" },
    { to: "/news", label: "News", icon: Rss, testid: "m-nav-news" },
    { to: "/skills", label: "Skills", icon: BookOpen, testid: "m-nav-skills" },
    { to: "/salaries", label: "Salaries", icon: DollarSign, testid: "m-nav-salaries" },
    { to: "/excellence", label: "Excellence", icon: Award, testid: "m-nav-excellence" },
    ...(authed ? [
      { to: "/events", label: "Events", icon: Calendar, testid: "m-nav-events" },
      { to: "/polls", label: "Polls", icon: BarChart3, testid: "m-nav-polls" },
      { to: "/messages", label: "DMs", icon: MessageSquare, testid: "m-nav-messages" },
    ] : []),
  ];

  return (
    <header data-testid="app-navbar" className="sticky top-0 z-40 bg-[hsl(var(--nh-bg))] border-b-2 border-black">
      <div className="max-w-7xl mx-auto flex items-center gap-4 px-4 sm:px-6 h-16">
        {/* Mobile hamburger — visible below md */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              data-testid="mobile-menu-btn"
              aria-label="Open navigation menu"
              className="md:hidden border-2 border-black rounded-none nh-hover-push h-10 w-10 shrink-0"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" data-testid="mobile-menu" className="w-[85vw] max-w-[320px] bg-[hsl(var(--nh-bg))] border-r-2 border-black p-0">
            <SheetHeader className="p-4 border-b-2 border-black">
              <SheetTitle className="font-black text-xl tracking-tight flex items-center gap-2">
                <div className="w-8 h-8 bg-[hsl(var(--nh-red))] border-2 border-black flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                NURSELINKHUB
              </SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col p-2">
              {mobileLinks.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-base font-semibold uppercase tracking-tight border-b border-black/10 transition-colors ${
                      isActive ? "bg-[hsl(var(--nh-red))] text-white" : "text-black hover:bg-black/5"
                    }`
                  }
                >
                  <Icon className="w-5 h-5" /> {label}
                </NavLink>
              ))}
              {authed && (
                <>
                  <NavLink to={`/nurses/${user.id}`} data-testid="m-nav-profile" className="flex items-center gap-3 px-4 py-3 text-base font-semibold uppercase border-b border-black/10 hover:bg-black/5">
                    <User className="w-5 h-5" /> My Profile
                  </NavLink>
                  <NavLink to="/subscribe" data-testid="m-nav-subscribe" className="flex items-center gap-3 px-4 py-3 text-base font-semibold uppercase border-b border-black/10 hover:bg-black/5">
                    <Sparkles className="w-5 h-5" /> Subscription
                  </NavLink>
                  {user.role === "admin" && (
                    <NavLink to="/admin" data-testid="m-nav-admin" className="flex items-center gap-3 px-4 py-3 text-base font-semibold uppercase border-b border-black/10 hover:bg-black/5">
                      <Shield className="w-5 h-5" /> Admin
                    </NavLink>
                  )}
                  <button
                    data-testid="m-nav-logout"
                    onClick={async () => { setMobileOpen(false); await logout(); nav("/"); }}
                    className="flex items-center gap-3 px-4 py-3 text-base font-semibold uppercase text-left hover:bg-black/5"
                  >
                    <LogOut className="w-5 h-5" /> Log out
                  </button>
                </>
              )}
              {!authed && (
                <div className="p-3 flex flex-col gap-2 mt-2">
                  <Link to="/login" data-testid="m-nav-login" onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" className="w-full border-2 border-black rounded-none nh-hover-push">Log in</Button>
                  </Link>
                  <Link to="/register" data-testid="m-nav-register" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full bg-black text-white rounded-none border-2 border-black nh-hover-push">Sign up</Button>
                  </Link>
                </div>
              )}
            </nav>
          </SheetContent>
        </Sheet>

        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <div className="w-8 h-8 bg-[hsl(var(--nh-red))] border-2 border-black flex items-center justify-center group-hover:rotate-6 transition-transform">
            <Zap className="w-4 h-4 text-white" strokeWidth={3} />
          </div>
          <div className="leading-tight">
            <span className="block font-black text-xl tracking-tight">NURSELINKHUB</span>
            <span className="hidden sm:block text-[9px] uppercase tracking-widest text-black/50" data-testid="brand-promise-nav">The professional home for nurses</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center ml-6">
          {authed && (
            <NavLink to="/dashboard" data-testid="nav-dashboard" className={linkCls}>
              <LayoutDashboard className="inline w-4 h-4 mr-1" /> Dashboard
            </NavLink>
          )}
          <NavLink to="/agencies" data-testid="nav-agencies" className={linkCls}>
            <Building2 className="inline w-4 h-4 mr-1" /> Agencies
          </NavLink>
          <NavLink to="/jobs" data-testid="nav-jobs" className={linkCls}>
            <Briefcase className="inline w-4 h-4 mr-1" /> Jobs
          </NavLink>
          <NavLink to="/feed" data-testid="nav-feed" className={linkCls}>
            <Newspaper className="inline w-4 h-4 mr-1" /> Feed
          </NavLink>
          <NavLink to="/forums" data-testid="nav-forums" className={linkCls}>
            <Users className="inline w-4 h-4 mr-1" /> Forums
          </NavLink>
          <NavLink to="/nurses" data-testid="nav-nurses" className={linkCls}>
            <Users className="inline w-4 h-4 mr-1" /> Nurses
          </NavLink>
          <NavLink to="/news" data-testid="nav-news" className={linkCls}>
            <Rss className="inline w-4 h-4 mr-1" /> News
          </NavLink>
          <NavLink to="/skills" data-testid="nav-skills" className={linkCls}>
            <BookOpen className="inline w-4 h-4 mr-1" /> Skills
          </NavLink>
          <NavLink to="/salaries" data-testid="nav-salaries" className={linkCls}>
            <DollarSign className="inline w-4 h-4 mr-1" /> Salaries
          </NavLink>
          <NavLink to="/excellence" data-testid="nav-excellence" className={linkCls}>
            <Award className="inline w-4 h-4 mr-1" /> Excellence
          </NavLink>
          {authed && (
            <NavLink to="/events" data-testid="nav-events" className={linkCls}>
              <Calendar className="inline w-4 h-4 mr-1" /> Events
            </NavLink>
          )}
          {authed && (
            <NavLink to="/polls" data-testid="nav-polls" className={linkCls}>
              <BarChart3 className="inline w-4 h-4 mr-1" /> Polls
            </NavLink>
          )}
          {authed && (
            <NavLink to="/messages" data-testid="nav-messages" className={linkCls}>
              <MessageSquare className="inline w-4 h-4 mr-1" /> DMs
            </NavLink>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {authed ? (
            <>
              {!active && (
                <Button
                  onClick={() => nav("/subscribe")}
                  data-testid="nav-subscribe-btn"
                  className="bg-[hsl(var(--nh-red))] hover:bg-[hsl(var(--nh-red-dark))] text-white border-2 border-black rounded-none nh-hover-push"
                >
                  <Sparkles className="w-4 h-4 mr-1" /> Activate $5/mo
                </Button>
              )}
              {active && <Badge data-testid="badge-active" className="bg-[hsl(var(--nh-mint))] text-black border-2 border-black rounded-none">ACTIVE</Badge>}
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="user-menu-btn" className="border-2 border-black rounded-none nh-hover-push">
                    {user.name?.split(" ")[0] || "Me"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="border-2 border-black rounded-none bg-white">
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => nav(`/nurses/${user.id}`)}>
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-subscribe" onClick={() => nav("/subscribe")}>
                    <Sparkles className="w-4 h-4 mr-2" /> Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-notif-settings" onClick={() => nav("/settings/notifications")}>
                    <Bell className="w-4 h-4 mr-2" /> Email preferences
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>
                      <Shield className="w-4 h-4 mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="outline" className="border-2 border-black rounded-none nh-hover-push">Log in</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-black text-white rounded-none border-2 border-black nh-hover-push">Sign up</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
